/**
 * ErpIntegrationFormDialog — F18 T18.1.b (Settings → Integracje).
 *
 * Modal create/edit dla integracji ERP (Symfonia). Pola muszą być spójne z
 * `createIntegrationSchema` / `updateIntegrationSchema` w
 * `apps/api/lib/erp-integrations/validators.ts`.
 *
 * Tryb edycji:
 *   - companyId READONLY (immutable — backend zwraca 400 jeśli body zawiera companyId)
 *   - webapiLogin / webapiPassword OPCJONALNE (placeholder „nie zmieniaj") — gdy puste,
 *     handler zostawia stare zaszyfrowane wartości. Tylko wpisane wartości lecą do PATCH
 *     i są re-encryptowane.
 *
 * Walidacja po stronie UI mirror'uje zod (te same ograniczenia długości/URL/zakresy).
 * Backend i tak ostatecznie waliduje — UI jest tylko UX.
 */

import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '@/components/Button';
import { Checkbox } from '@/components/Checkbox';
import { Dropdown } from '@/components/Dropdown';
import { FormField } from '@/components/FormField';
import { Input } from '@/components/Input';
import { Modal } from '@/components/Modal';
import { CompanyPicker, type CompanyPickerOption } from '@/components/ui/CompanyPicker';

/**
 * Pełna unia wszystkich prawidłowych modułów WebAPI (Handel + FK) — typ pokrywa
 * oba zestawy. Które checkboxy realnie widać zależy od wybranego systemu ERP
 * (`HANDEL_MODULES` / `FK_MODULES` poniżej).
 */
export const ERP_WEBAPI_MODULES = [
  'contractors',
  'products',
  'purchases',
  'sales',
  'warehouse',
  'employees',
  'bank_accounts',
  'account_balances',
  'dictionaries',
] as const;
export type ErpWebapiModule = (typeof ERP_WEBAPI_MODULES)[number];

/** Moduły Symfonia Handel — magazynowo-handlowy świat. */
export const HANDEL_MODULES = [
  'contractors',
  'products',
  'purchases',
  'sales',
  'warehouse',
] as const satisfies readonly ErpWebapiModule[];

/** Moduły Symfonia FK — świat księgowy. */
export const FK_MODULES = [
  'contractors',
  'employees',
  'bank_accounts',
  'account_balances',
  'dictionaries',
] as const satisfies readonly ErpWebapiModule[];

/** Zwraca listę modułów właściwą dla wybranego systemu ERP. */
export function modulesForSystem(system: ErpSystem): readonly ErpWebapiModule[] {
  return system === 'symfonia_fk' ? FK_MODULES : HANDEL_MODULES;
}

export const ERP_SYNC_SCHEDULES = ['manual', '15min', '30min', 'hourly', 'daily'] as const;
export type ErpSyncSchedule = (typeof ERP_SYNC_SCHEDULES)[number];

export const ERP_SYSTEMS = ['symfonia_handel', 'symfonia_fk'] as const;
export type ErpSystem = (typeof ERP_SYSTEMS)[number];

export const ERP_CONNECTION_MODES = ['agent', 'direct'] as const;
export type ErpConnectionMode = (typeof ERP_CONNECTION_MODES)[number];

export interface ErpIntegrationFormValues {
  companyId: string;
  name: string;
  erpSystem: ErpSystem;
  connectionMode: ErpConnectionMode;
  webapiUrl: string;
  webapiVersion: string;
  /**
   * Application GUID — realny sekret auth Symfonia WebAPI. W trybie create: wymagane.
   * W trybie edit: puste = zachowaj obecne (handler je pomija).
   */
  applicationGuid: string;
  /** Nazwa urządzenia rejestrowana w Symfonia WebAPI (jawna, nie sekret). */
  deviceName: string;
  symfoniaCompany: string;
  enabledModules: ErpWebapiModule[];
  maxSessions: number;
  syncSchedule: ErpSyncSchedule;
  isActive: boolean;
}

/**
 * Alias zachowany dla wstecznej zgodności nazwy — kształt jest teraz tożsamy z
 * `CompanyPickerOption` (`{id, legalName}`), żeby pole firmy mogło reużyć
 * współdzielony `CompanyPicker` (spójny z DocumentTypeFormModal — Etap "wymagana
 * konkretna firma przy tworzeniu w trybie 'Wszystkie firmy'").
 */
export type CompanyOption = CompanyPickerOption;

interface Props {
  open: boolean;
  mode: 'create' | 'edit';
  companies: CompanyOption[];
  initialValues: ErpIntegrationFormValues;
  saving?: boolean;
  onSubmit: (values: ErpIntegrationFormValues) => void;
  onClose: () => void;
}

const URL_REGEX = /^https?:\/\/[^\s]+$/i;
const NAME_MAX = 100;

/**
 * Migawka wartości formularza do porównania „czy coś zmieniono". Moduły sortujemy, bo odznaczenie
 * i ponowne zaznaczenie modułu dokłada go na koniec listy — logicznie ten sam formularz.
 */
function snapshot(values: ErpIntegrationFormValues): string {
  return JSON.stringify({ ...values, enabledModules: [...values.enabledModules].sort() });
}

export function ErpIntegrationFormDialog({
  open,
  mode,
  companies,
  initialValues,
  saving,
  onSubmit,
  onClose,
}: Props) {
  const { t } = useTranslation();
  const [values, setValues] = useState<ErpIntegrationFormValues>(initialValues);
  const [errors, setErrors] = useState<Record<string, string>>({});
  // Migawka wartości z chwili otwarcia — do jawnego `dirty` (pytanie przy zamknięciu tłem/Esc).
  // Nie porównujemy z `initialValues`, bo rodzic przelicza je przy otwartym oknie (patrz niżej).
  const [baseline, setBaseline] = useState<string>(() => snapshot(initialValues));

  // Seeduje `values` z `initialValues` TYLKO na przejściu closed→open (deps
  // celowo bez `initialValues`) — rodzic (IntegrationsTab) liczy
  // `createInitialValues` przez `useMemo` keyed na aktywnej firmie, więc
  // przełączenie firmy przez usera przy otwartym dialogu tworzy nową referencję
  // `initialValues` co render. Gdyby ten efekt reagował na `initialValues` w
  // deps, każde takie przełączenie kasowałoby już wpisane pola (name/URL/login/
  // hasło) w trakcie edycji. Domyślna firma jest seedowana wyłącznie przy
  // otwarciu dialogu (mirror guard z CreateEmployeeModal w Employees.tsx).
  useEffect(() => {
    if (open) {
      setValues(initialValues);
      setBaseline(snapshot(initialValues));
      setErrors({});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  if (!open) return null;

  function update<K extends keyof ErpIntegrationFormValues>(
    key: K,
    val: ErpIntegrationFormValues[K],
  ) {
    setValues((prev) => ({ ...prev, [key]: val }));
  }

  // Zmiana systemu ERP przestawia listę dostępnych modułów — odfiltrowujemy
  // wcześniej zaznaczone moduły, które nie należą do nowo wybranego systemu,
  // żeby nigdy nie wysłać do backendu modułu nieważnego dla danego systemu.
  function changeErpSystem(system: ErpSystem) {
    const allowed = modulesForSystem(system);
    setValues((prev) => ({
      ...prev,
      erpSystem: system,
      enabledModules: prev.enabledModules.filter((m) => allowed.includes(m)),
    }));
  }

  function toggleModule(mod: ErpWebapiModule) {
    setValues((prev) => {
      const has = prev.enabledModules.includes(mod);
      return {
        ...prev,
        enabledModules: has
          ? prev.enabledModules.filter((m) => m !== mod)
          : [...prev.enabledModules, mod],
      };
    });
  }

  function validate(): boolean {
    const next: Record<string, string> = {};
    if (!values.companyId)
      next.companyId = t('tenant.settings.integrations.form.errors.companyRequired');
    if (!values.name.trim()) next.name = t('tenant.settings.integrations.form.errors.nameRequired');
    else if (values.name.length > NAME_MAX)
      next.name = t('tenant.settings.integrations.form.errors.nameTooLong');
    if (values.webapiUrl && !URL_REGEX.test(values.webapiUrl.trim()))
      next.webapiUrl = t('tenant.settings.integrations.form.errors.urlInvalid');
    if (mode === 'create') {
      if (!values.applicationGuid.trim())
        next.applicationGuid = t(
          'tenant.settings.integrations.form.errors.applicationGuidRequired',
        );
    }
    if (!values.symfoniaCompany.trim())
      next.symfoniaCompany = t('tenant.settings.integrations.form.errors.symfoniaCompanyRequired');
    if (values.maxSessions < 1 || values.maxSessions > 1000)
      next.maxSessions = t('tenant.settings.integrations.form.errors.maxSessionsRange');
    setErrors(next);
    return Object.keys(next).length === 0;
  }

  function handleSubmit() {
    if (!validate()) return;
    // Trim before emit. Form parent decides what to send to API (PATCH skips
    // empty credentials, POST sends them all).
    onSubmit({
      ...values,
      name: values.name.trim(),
      webapiUrl: values.webapiUrl.trim(),
      webapiVersion: values.webapiVersion.trim(),
      applicationGuid: values.applicationGuid.trim(),
      deviceName: values.deviceName.trim(),
      symfoniaCompany: values.symfoniaCompany.trim(),
    });
  }

  const isEdit = mode === 'edit';
  const isDirty = snapshot(values) !== baseline;

  return (
    <Modal
      open
      onClose={onClose}
      title={
        isEdit
          ? t('tenant.settings.integrations.form.titleEdit')
          : t('tenant.settings.integrations.form.titleCreate')
      }
      size="lg"
      testId="erp-integration-form-modal"
      guardUnsaved
      dirty={isDirty}
      closeOnBackdrop={!saving}
      closeOnEsc={!saving}
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={saving}>
            {t('common.cancel')}
          </Button>
          <Button
            variant="primary"
            onClick={handleSubmit}
            loading={saving}
            data-testid="erp-integration-form-submit"
          >
            {isEdit ? t('common.save') : t('common.create')}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <FormField
          label={t('tenant.settings.integrations.form.fields.company')}
          error={errors.companyId}
        >
          <CompanyPicker
            value={values.companyId || null}
            onChange={(v) => update('companyId', v ?? '')}
            companies={companies}
            disabled={isEdit || companies.length <= 1}
            required
            testId="erp-integration-form-companyId"
          />
        </FormField>

        <FormField label={t('tenant.settings.integrations.form.fields.erpSystem')}>
          <Dropdown
            value={values.erpSystem}
            onChange={(v) => changeErpSystem(v as ErpSystem)}
            data-testid="erp-integration-form-erpSystem"
            options={ERP_SYSTEMS.map((sys) => ({
              value: sys,
              label: t(`tenant.settings.integrations.form.erpSystems.${sys}`),
            }))}
          />
        </FormField>

        <FormField label={t('tenant.settings.integrations.form.fields.name')} error={errors.name}>
          <Input
            value={values.name}
            onChange={(e) => update('name', e.target.value)}
            placeholder={t('tenant.settings.integrations.form.fields.namePlaceholder')}
            data-testid="erp-integration-form-name"
          />
        </FormField>

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <FormField
            label={t('tenant.settings.integrations.form.fields.webapiUrl')}
            hint={t('tenant.settings.integrations.form.fields.webapiUrlHint')}
            error={errors.webapiUrl}
          >
            <Input
              value={values.webapiUrl}
              onChange={(e) => update('webapiUrl', e.target.value)}
              placeholder="http://localhost:9000"
              data-testid="erp-integration-form-webapiUrl"
            />
          </FormField>

          <FormField
            label={t('tenant.settings.integrations.form.fields.webapiVersion')}
            hint={t('tenant.settings.integrations.form.fields.webapiVersionHint')}
          >
            <Input
              value={values.webapiVersion}
              onChange={(e) => update('webapiVersion', e.target.value)}
              placeholder="2025"
              data-testid="erp-integration-form-webapiVersion"
            />
          </FormField>
        </div>

        <FormField
          label={t('tenant.settings.integrations.form.fields.connectionMode')}
          hint={t('tenant.settings.integrations.form.fields.connectionModeHint')}
        >
          <Dropdown
            value={values.connectionMode}
            onChange={(v) => update('connectionMode', v as ErpConnectionMode)}
            data-testid="erp-integration-form-connectionMode"
            options={ERP_CONNECTION_MODES.map((m) => ({
              value: m,
              label: t(`tenant.settings.integrations.form.connectionModes.${m}`),
            }))}
          />
        </FormField>

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <FormField
            label={t('tenant.settings.integrations.form.fields.applicationGuid')}
            hint={
              isEdit
                ? t('tenant.settings.integrations.form.fields.credentialsEditHint')
                : t('tenant.settings.integrations.form.fields.applicationGuidHint')
            }
            error={errors.applicationGuid}
          >
            <Input
              type="password"
              value={values.applicationGuid}
              onChange={(e) => update('applicationGuid', e.target.value)}
              placeholder={isEdit ? '••••••••' : ''}
              autoComplete="off"
              data-testid="erp-integration-form-applicationGuid"
            />
          </FormField>

          <FormField
            label={t('tenant.settings.integrations.form.fields.deviceName')}
            hint={t('tenant.settings.integrations.form.fields.deviceNameHint')}
          >
            <Input
              value={values.deviceName}
              onChange={(e) => update('deviceName', e.target.value)}
              autoComplete="off"
              data-testid="erp-integration-form-deviceName"
            />
          </FormField>
        </div>

        <FormField
          label={t('tenant.settings.integrations.form.fields.symfoniaCompany')}
          hint={t('tenant.settings.integrations.form.fields.symfoniaCompanyHint')}
          error={errors.symfoniaCompany}
        >
          <Input
            value={values.symfoniaCompany}
            onChange={(e) => update('symfoniaCompany', e.target.value)}
            data-testid="erp-integration-form-symfoniaCompany"
          />
        </FormField>

        <FormField
          label={t('tenant.settings.integrations.form.fields.enabledModules')}
          hint={t('tenant.settings.integrations.form.fields.enabledModulesHint')}
        >
          <div
            className="grid grid-cols-2 gap-2 sm:grid-cols-3"
            data-testid="erp-integration-form-modules"
          >
            {modulesForSystem(values.erpSystem).map((mod) => (
              <Checkbox
                key={mod}
                checked={values.enabledModules.includes(mod)}
                onChange={() => toggleModule(mod)}
                label={t(`tenant.settings.integrations.form.modules.${mod}`)}
                data-testid={`erp-integration-form-module-${mod}`}
              />
            ))}
          </div>
        </FormField>

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <FormField
            label={t('tenant.settings.integrations.form.fields.maxSessions')}
            hint={t('tenant.settings.integrations.form.fields.maxSessionsHint')}
            error={errors.maxSessions}
          >
            <Input
              type="number"
              min={1}
              max={1000}
              value={values.maxSessions}
              onChange={(e) => update('maxSessions', Number(e.target.value))}
              data-testid="erp-integration-form-maxSessions"
            />
          </FormField>

          <FormField label={t('tenant.settings.integrations.form.fields.syncSchedule')}>
            <Dropdown
              value={values.syncSchedule}
              onChange={(v) => update('syncSchedule', v as ErpSyncSchedule)}
              data-testid="erp-integration-form-syncSchedule"
              options={ERP_SYNC_SCHEDULES.map((s) => ({
                value: s,
                label: t(`tenant.settings.integrations.form.syncSchedules.${s}`),
              }))}
            />
          </FormField>
        </div>

        <Checkbox
          checked={values.isActive}
          onChange={(e) => update('isActive', e.target.checked)}
          label={t('tenant.settings.integrations.form.fields.isActive')}
          data-testid="erp-integration-form-isActive"
        />
      </div>
    </Modal>
  );
}

export const DEFAULT_FORM_VALUES: ErpIntegrationFormValues = {
  companyId: '',
  name: '',
  erpSystem: 'symfonia_handel',
  connectionMode: 'agent',
  webapiUrl: 'http://localhost:9000',
  webapiVersion: '2025',
  applicationGuid: '',
  deviceName: 'Workflow365',
  symfoniaCompany: '',
  enabledModules: ['contractors', 'products', 'purchases'],
  maxSessions: 10,
  syncSchedule: 'manual',
  isActive: true,
};
